import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Check, X, SlidersHorizontal } from 'lucide-react';
import { loadGA4 } from '../lib/analytics';

const STORAGE_KEY = 'empax-cookie-consent';
export const OPEN_EVENT = 'empax:openCookieSettings';

// Read stored consent. Returns null if the visitor has not decided yet.
const readConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    if (raw === 'accepted') return { necessary: true, analytics: true }; // legacy value
    const parsed = JSON.parse(raw);
    return { necessary: true, analytics: !!parsed.analytics };
  } catch {
    return null;
  }
};

const saveConsent = (analytics) => {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ necessary: true, analytics, ts: new Date().toISOString() })
  );
};

const Toggle = ({ checked, disabled, onChange, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    data-testid={testid}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-300 ${
      checked ? 'bg-[var(--accent)]' : 'bg-[var(--surface)] border border-[var(--border-strong)]'
    } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-[#0A0D17] transition-transform duration-300 ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [manage, setManage] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const consent = readConsent();
    if (consent) {
      if (consent.analytics) loadGA4();
      return;
    }
    // First-time visitor: keep the banner hidden until the intro splash has finished,
    // then reveal it after a short delay. On later visits (intro already seen) use a small delay.
    let introSeen = true;
    try { introSeen = sessionStorage.getItem('empax_intro_seen') === '1'; } catch { introSeen = true; }
    if (introSeen) {
      const t = setTimeout(() => setVisible(true), 1400);
      return () => clearTimeout(t);
    }
    let afterIntro;
    const onIntroDone = () => { afterIntro = setTimeout(() => setVisible(true), 600); };
    window.addEventListener('empax:introDone', onIntroDone, { once: true });
    // Safety fallback in case the intro event is missed.
    const fallback = setTimeout(() => setVisible(true), 9200);
    return () => {
      window.removeEventListener('empax:introDone', onIntroDone);
      clearTimeout(afterIntro);
      clearTimeout(fallback);
    };
  }, []);

  // Allow reopening the preferences from anywhere (e.g. footer "Cookie Settings").
  useEffect(() => {
    const open = () => {
      const consent = readConsent();
      setAnalytics(consent ? consent.analytics : false);
      setManage(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);

  const acceptAll = () => {
    saveConsent(true);
    loadGA4();
    setVisible(false);
  };

  const rejectAll = () => {
    saveConsent(false);
    setVisible(false);
  };

  const savePreferences = () => {
    saveConsent(analytics);
    if (analytics) loadGA4();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent"
      className="fixed bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-6 z-[9998] animate-cookie-in"
    >
      <div className="relative border border-[var(--border-strong)] rounded-2xl px-6 py-5 md:px-9 md:py-6 shadow-2xl" style={{ backgroundColor: 'var(--brand-blue)' }}>
        <span className="pointer-events-none absolute top-3 left-3 w-4 h-4 border-l border-t border-[var(--accent)]/50" />
        <span className="pointer-events-none absolute bottom-3 right-3 w-4 h-4 border-r border-b border-[var(--accent)]/50" />

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 md:gap-10">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 text-[var(--accent)]"><Cookie size={20} strokeWidth={1.6} /></span>
            <div>
              <p className="label mb-1.5">We value your privacy</p>
              <p className="text-[var(--muted)] leading-relaxed font-light">
                We use cookies to give you the best experience on our website.{' '}
                <Link to="/privacy" data-testid="cookie-privacy-link" className="text-[var(--accent)] hover:underline">
                  Learn more
                </Link>
              </p>
            </div>
          </div>

          {!manage && (
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-accept"
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border-strong)] text-[var(--text)] font-mono tracking-wide uppercase px-5 py-2.5 hover:bg-[var(--accent)] hover:text-[#0A0D17] hover:border-[var(--accent)] transition-all"
              >
                <Check size={15} /> Accept
              </button>
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-reject"
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border-strong)] text-[var(--text)] font-mono tracking-wide uppercase px-5 py-2.5 hover:bg-[var(--surface)] transition-all"
              >
                <X size={15} /> Reject
              </button>
              <button
                type="button"
                onClick={() => setManage(true)}
                data-testid="cookie-manage"
                className="inline-flex items-center gap-2 nav-link label hover:text-[var(--text)] transition-colors px-2 py-2.5"
              >
                <SlidersHorizontal size={14} /> Manage
              </button>
            </div>
          )}
        </div>

        {manage && (
          <>
            <div data-testid="cookie-preferences" className="mt-5 space-y-4 border-t border-[var(--border)] pt-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-[var(--text)] font-medium">Strictly necessary</p>
                  <p className="text-[var(--muted)] text-sm font-light">Required for the site to function. Always on.</p>
                </div>
                <Toggle checked disabled testid="cookie-necessary-toggle" onChange={() => {}} />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-[var(--text)] font-medium">Analytics</p>
                  <p className="text-[var(--muted)] text-sm font-light">Helps us measure and improve the experience.</p>
                </div>
                <Toggle checked={analytics} onChange={setAnalytics} testid="cookie-analytics-toggle" />
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={savePreferences}
                data-testid="cookie-save-prefs"
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border-strong)] text-[var(--text)] font-mono tracking-wide uppercase px-5 py-2.5 hover:bg-[var(--accent)] hover:text-[#0A0D17] hover:border-[var(--accent)] transition-all"
              >
                <Check size={15} /> Save preferences
              </button>
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-reject"
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border-strong)] text-[var(--text)] font-mono tracking-wide uppercase px-5 py-2.5 hover:bg-[var(--surface)] transition-all"
              >
                <X size={15} /> Reject
              </button>
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-accept"
                className="inline-flex items-center gap-2 nav-link label hover:text-[var(--text)] transition-colors px-2 py-2.5"
              >
                Accept all
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Helper other components can import to reopen the preferences panel.
export const openCookieSettings = () => window.dispatchEvent(new Event(OPEN_EVENT));

export default CookieConsent;
